import { Map } from "ol";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  PointOfInterestType,
  PTOPProjectPOIDto,
  useProjectApi,
} from "~/api/GeneratedApi";
import MiscIcon from "~/assets/img/question-mark.svg";
import ShipWreckIcon from "~/assets/img/shipwreck.svg";
import DangerIcon from "~/assets/img/warning-icon-red.svg";
import { useCustomMutation } from "~/utils/hooks/useCustomMutation";
import { staleTime30mins, useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { useCustomQueryClient } from "~/utils/hooks/useCustomQueryClient";
import { drawPoiItems } from "../utils/drawPoiItems";
import { useToggleAnimation } from "./useToggleAnimation";

export type PoiMarker = {
  type: string;

  description: string;
  date: Date;
  coordinates: number[];
};

export const getPoiIcon = (poiType: PointOfInterestType | undefined) => {
  switch (poiType) {
    case PointOfInterestType.Wreck:
      return ShipWreckIcon;
    case PointOfInterestType.Danger:
      return DangerIcon;
    case PointOfInterestType.Misc:
      return MiscIcon;
    default:
      return undefined;
  }
};

export type PoiEnumType = keyof typeof PointOfInterestType;
export const poiTypes = Object.keys(PointOfInterestType)?.filter((k) =>
  Number.isNaN(Number(k)),
) as PoiEnumType[];

export const usePoiItems = ({ id }: { id: number | undefined }) => {
  const { getPOIs, addPOI, removePOI } = useProjectApi();
  const qc = useCustomQueryClient();
  const { data: poiItems } = useCustomQuery(
    { key: "poi-items", id },
    () => getPOIs(id as number),
    {
      enabled: id !== undefined,
      staleTime: staleTime30mins,
    },
  );

  const [selectedPoiItemType, setSelectedPoiItem] = React.useState<
    keyof typeof PointOfInterestType
  >(poiTypes[0]);

  const poiIcon = getPoiIcon(PointOfInterestType[selectedPoiItemType]);

  const { mutateAsync: addPoiItem } = useCustomMutation(
    ({
      projectId,
      poiItem,
    }: {
      projectId: number;
      poiItem: PTOPProjectPOIDto;
    }) => addPOI(poiItem, projectId),
    {
      onSuccess: (_l, { projectId }) => {
        void qc.invalidateCustomQueries({ key: "poi-items", id: projectId });
      },
    },
  );

  const { mutate: deletePoiItem } = useCustomMutation(
    ({ projectId, poiItemId }: { projectId: number; poiItemId: number }) =>
      removePOI(projectId, poiItemId),
    {
      onSuccess: (_l, { projectId }) => {
        void qc.invalidateCustomQueries({ key: "poi-items", id: projectId });
      },
    },
  );

  return {
    poiItems,
    selectedPoiItem: selectedPoiItemType,
    setSelectedPoiItem,
    poiIcon,
    addPoiItem,
    deletePoiItem,
  };
};

type UsePoiItemDraw = {
  enabled: boolean;
  map: Map | undefined;
  markers: PTOPProjectPOIDto[] | undefined;
  toggleAnimation: boolean;
  disableAnimation?: boolean;
};

export const usePoiItemDraw = ({
  enabled,
  map,
  markers,
  toggleAnimation,
  disableAnimation = false,
}: UsePoiItemDraw) => {
  // TOGGLE ANIMATION WHEN SHOWING / HIDING POI MARKERS
  const { setShowAnimation, showAnimation } = useToggleAnimation({
    toggleAnimation,
  });
  const { t } = useTranslation();
  React.useEffect(() => {
    if (!map || !markers) return;
    drawPoiItems({
      map,
      poiMarkers: markers,
      enabled,
      showAnimation: false,
      setShowAnimation,
      disableAnimation,
      t,
    });
  }, [
    disableAnimation,
    enabled,
    map,
    markers,
    setShowAnimation,
    showAnimation,
    t,
  ]);
};
